import React, {useState, useEffect, useRef} from 'react';
import {
    Box,
    Button,
    TextField,
    IconButton,
    CircularProgress,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {toast} from 'react-toastify';
import {adminApi} from 'utils/api';

export default function EnvVarsTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [focusIndex, setFocusIndex] = useState(-1);
    const keyRefs = useRef([]);

    const fetchEnvVars = async () => {
        try {
            const res = await adminApi.getEnvVars();
            const list = res.data.envVars.map(e => ({key: e.key, value: e.value}));
            setRows(list.length > 0 ? list : [{key: '', value: ''}]);
        } catch (err) {
            toast.error('환경 변수 목록을 불러오지 못했습니다.');
            setRows([{key: '', value: ''}]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnvVars();
    }, []);

    useEffect(() => {
        if (focusIndex >= 0 && keyRefs.current[focusIndex]) {
            keyRefs.current[focusIndex].focus();
            setFocusIndex(-1);
        }
    }, [focusIndex, rows.length]);

    const handleChange = (index, field, val) => {
        setRows(prev => prev.map((r, i) => i === index ? {...r, [field]: val} : r));
    };

    const handleAdd = () => {
        setRows(prev => [...prev, {key: '', value: ''}]);
        setFocusIndex(rows.length);
    };

    const handleRemove = (index) => {
        setRows(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        const validRows = rows.filter(r => r.key.trim());
        if (validRows.length !== rows.length) {
            toast.warning('KEY가 비어있는 항목이 있습니다.');
            return;
        }
        setSaving(true);
        try {
            const res = await adminApi.saveEnvVars(validRows);
            setRows(res.data.envVars.map(e => ({key: e.key, value: e.value})));
            toast.success('환경 변수가 저장되었습니다.');
        } catch (err) {
            toast.error(err.response?.data?.error || '저장 실패');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}><CircularProgress/></Box>;
    }

    return (
        <Box>
            <Typography variant="h6" sx={{mb: 2}}>환경 변수 관리</Typography>

            {rows.map((row, index) => {
                const isLast = index === rows.length - 1;
                return (
                    <Box key={index} sx={{display: 'flex', gap: 1, mb: 1, alignItems: 'center'}}>
                        <IconButton
                            color="error"
                            onClick={() => handleRemove(index)}
                            disabled={rows.length <= 1}
                            sx={{width: 50, height: 50}}
                        >
                            <RemoveIcon/>
                        </IconButton>
                        <TextField
                            size="small"
                            value={row.key}
                            onChange={(e) => handleChange(index, 'key', e.target.value)}
                            inputRef={el => keyRefs.current[index] = el}
                            placeholder="KEY"
                            label="KEY"
                            sx={{flex: 1, minWidth: 200, maxWidth: 350}}
                        />
                        <TextField
                            size="small"
                            value={row.value}
                            onChange={(e) => handleChange(index, 'value', e.target.value)}
                            placeholder="VALUE"
                            label="VALUE"
                            sx={{flex: 2, minWidth: 0}}
                        />
                        {isLast && (
                            <IconButton
                                color="primary"
                                onClick={handleAdd}
                                sx={{width: 50, height: 50}}
                            >
                                <AddIcon/>
                            </IconButton>
                        )}
                    </Box>
                );
            })}

            <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 1}}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? '저장 중...' : '저장'}
                </Button>
            </Box>
        </Box>
    );
}
